import { Badge } from '@biq/apex-ui';
import { Grid, Typography } from '@mui/material';
import { themeObject } from 'lib';

export type StatusType = 'success' | 'warning' | 'error';

export const SUCCESS_CODE = 200;
export const SUCCESS_MESSAGE = 'Operating as expected.';
export const ERROR_MESSAGE = 'Service outage detected.';
export const WARNING_MESSAGE =
    'Performance is degraded, but no outages are detected.';
export const ENHANCED_WARNING_MESSAGE = `${WARNING_MESSAGE} This could be due to latency between you and the network.`;

/**
 * NOTE: There's an issue where the server takes too long if it has been idle or without
 * a connection for a while. Hence, the first response takes anything between 600ms - 1s.
 * Thus, we are increasing the limit until the server team can investigate a potential
 * fix for this issue.
 */
export const RESPONSE_TIME_LIMIT_IN_MILLISECONDS = 1500;

export const styles = {
    mainContainer: {
        mb: { xs: 4, md: 0 },
    },
    badgeStyles: {
        borderRadius: '10px',
        height: '12px',
        width: '12px',
    },
    headingWithBadgeComponentContainerStyles: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    headingTextStyles: {
        ...themeObject.typography.h5,
        textTransform: 'uppercase',
        lineHeight: '21.13px',
        color: themeObject.palette.grayScale[800],
        ml: 1,
    },
    successMessageStyles: {
        ...themeObject.typography.body.default,
        ...themeObject.typography.body.body1,
        ...themeObject.typography.body.regular,
        lineHeight: '21.17px',
        color: themeObject.palette.grayScale[800],
        mt: 1,
    },
    warningAndErrorMessageStyles: {
        ...themeObject.typography.body.default,
        ...themeObject.typography.body.body2,
        ...themeObject.typography.body.regular,
        lineHeight: '18.52px',
        color: themeObject.palette.grayScale[800],
        mt: '4px',
        zIndex: 2,
        position: 'relative',
    },
    underlineAndBoldTextStyles: {
        fontWeight: 500,
        fontSize: '14px',
        position: 'relative',
        textWrap: 'nowrap',
        cursor: 'pointer',
        '&:after': {
            content: '""',
            height: '2px',
            width: '100%',
            position: 'absolute',
            backgroundColor: themeObject.palette.primary[300],
            left: 0,
            bottom: '3.4px',
            zIndex: -1,
        },
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '120px',
    },
};

export const HeadingWithBadgeComponent = (props: {
    title: string;
    status: StatusType | null;
}): React.JSX.Element => {
    const { title, status } = props;
    let badgeColor = '';
    if (status === 'success') {
        badgeColor = themeObject.palette.tertiary.green[400];
    } else if (status === 'warning') {
        badgeColor = themeObject.palette.tertiary.yellow[400];
    } else if (status === 'error') {
        badgeColor = themeObject.palette.tertiary.red[300];
    }
    return (
        <Grid sx={styles.headingWithBadgeComponentContainerStyles}>
            <Badge
                sx={{
                    ...styles.badgeStyles,
                    backgroundColor: badgeColor,
                }}
            />
            <Typography sx={styles.headingTextStyles}>{title}</Typography>
        </Grid>
    );
};

export const StatusMessageComponent = (props: {
    status: StatusType | null;
    enhanceWarning?: boolean;
}): React.JSX.Element => {
    const { status, enhanceWarning = false } = props;
    if (status === 'success') {
        return (
            <Typography sx={styles.successMessageStyles}>
                {SUCCESS_MESSAGE}
            </Typography>
        );
    } else if (status === 'warning') {
        return (
            <Typography sx={styles.warningAndErrorMessageStyles}>
                {enhanceWarning ? ENHANCED_WARNING_MESSAGE : WARNING_MESSAGE}
            </Typography>
        );
    } else if (status === 'error') {
        return (
            <Typography sx={styles.warningAndErrorMessageStyles}>
                {ERROR_MESSAGE}
            </Typography>
        );
    } else {
        return <></>;
    }
};
